import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import { IFilterState, QuickFilter, SearchResultContext } from './index.context'
import { useLocation, useNavigate } from 'react-router-dom'
import { FormValues } from '../all-transactions/index.context'
import { useTranslation } from 'react-i18next'
import { IOption } from 'src/app/shared/models/common.model'
import { IGetTransactions } from 'src/app/shared/models/Transaction.model'
import { getTransactions } from 'src/app/shared/services/TransactionService'
import { STATUS_CODES } from 'src/app/shared/constants/common.constants'
import { TABLE_NAME } from 'src/app/shared/constants/tableName.constants'
import { formatNumberWithCommas } from 'src/app/shared/utils'
import StatusTag from './components/StatusTag'
import { TRANSACTION_STATUS_LABEL } from './index.constant'
import { outerTableRef } from 'src/app/shared/components/common/new-table/components/TableProvider'
import { menuUrls } from 'src/app/shared/constants/menu.constants'
import { TableColumnDefs } from 'src/app/shared/components/common/new-table/table.model'
import { useTransactionFilters } from '../TransactionFiltersContext'

const SearchResultProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { state }: { state: FormValues } = useLocation()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { saveSearchParams, restoreSearchParams } = useTransactionFilters()
  
  // Initialize defaultParams directly from sessionStorage to avoid timing issues
  const [defaultParams] = useState<any>(() => {
    const savedParams = restoreSearchParams()
    return savedParams || {}
  })
  
  const [filterState, setFilterState] = useState<IFilterState>({
    disabledFilter: [],
    selectedFilter: state?.transactionType ?? QuickFilter.ALL,
  })
  const [loading, setLoading] = useState<boolean>(true)
  const [transactionState, setTransactionState] = useState<IGetTransactions.Response>({
    items: [],
    total: 0,
  })

  const generateCustomColumnDefs = useCallback((colDefs: Array<TableColumnDefs>): Array<TableColumnDefs> => {
    return colDefs.map((item): TableColumnDefs => {
      if (item.colId === 'imageFound') {
        item.cellRenderer = (data: any) => (
          <StatusTag status={data.data.imageFound ? TRANSACTION_STATUS_LABEL.TRUE : TRANSACTION_STATUS_LABEL.FALSE} />
        )
      }
      if (item.colId === 'payeeMatchConfidence') {
        item.cellRenderer = (data: any) => {
          const value = data?.value
          if (value) {
            const payeeMatchValue = value?.split('%')[0]
            const payeeNumberFormat = Number(payeeMatchValue) ? Number(payeeMatchValue)?.toFixed(2) : 0
            return `${payeeNumberFormat}%`
          }
          return ''
        }
      }
      if (item.colId === 'status') {
        item.cellRenderer = (data: any) => <StatusTag status={data.data.status} />
      }
      if (
        ['accountNumber', 'checkNumber', 'achOriginatorID', 'amount', 'traceNumber', 'transactionCode'].includes(
          item.colId,
        )
      ) {
        item.dataType = 'number'
      }
      if (
        [
          'issuedDate',
          'inputDate',
          'paidDate',
          'voidDate',
          'exceptionDate',
          'staleDatedAsOf',
          'status',
          'postedDate',
        ].includes(item.colId)
      ) {
        item.dataType = 'date'
      }
      if (['issuedDate', 'inputDate', 'paidDate', 'voidDate', 'exceptionDate', 'postedDate'].includes(item.colId)) {
        item.valueType = 'date'
      }
      return item
    })
  }, [])

  const filterOptions: IOption[] = useMemo(
    () => [
      {
        label: t('TransactionResult:title.ach'),
        value: QuickFilter.ACH,
      },
      {
        label: t('TransactionResult:title.checks'),
        value: QuickFilter.CHECKS,
      },
      {
        label: t('TransactionResult:title.voids'),
        value: QuickFilter.VOIDS,
      },
      {
        label: t('TransactionResult:title.stale_dated'),
        value: QuickFilter.STALE_DATED,
      },
      {
        label: t('TransactionResult:title.payee_match'),
        value: QuickFilter.PAYEE_MATCH,
      },
      {
        label: t('TransactionResult:title.issued'),
        value: QuickFilter.ISSUED,
      },
    ],
    [t],
  )

  const formatFormValues = useCallback(
    (state: FormValues): Partial<IGetTransactions.Payload> => {
      const data: Partial<IGetTransactions.Payload> = {
        ...state,
        transactionType: filterState.selectedFilter,
        clientIds: state.clientIds ?? [],
        accountIds: state.accountIds ?? [],
        amountFrom: state.amountFrom ? parseFloat(state.amountFrom) : (null as any),
        amountTo: state.amountTo ? parseFloat(state.amountTo) : (null as any),
        decision: state.decisionId ?? '',
        reasons: state.reasonId ?? [],
        checkNumberFrom: state.checkNumberFrom ? parseFloat(state.checkNumberFrom) : (null as any),
        checkNumberTo: state.checkNumberTo ? parseFloat(state.checkNumberTo) : (null as any),
      }
      if (state.date && state.date.length > 0) {
        data.dateFrom = state.date[0] as string
        data.dateTo = state.date[1] as string
      }
      if (state.paidDate && state.paidDate.length > 0) {
        data.paidDateFrom = state.paidDate[0] as string
        data.paidDateTo = state.paidDate[1] as string
      }
      return data
    },
    [filterState.selectedFilter],
  )

  const getTableName = useCallback((filter: number) => {
    switch (filter as any) {
      case QuickFilter.ALL:
        return TABLE_NAME.ALL_TRANSACTION_RESULTS
      case QuickFilter.ACH:
        return TABLE_NAME.ALL_TRANSACTION_RESULTS_ACH
      case QuickFilter.CHECKS:
        return TABLE_NAME.ALL_TRANSACTION_RESULTS_CHECKS
      case QuickFilter.VOIDS:
        return TABLE_NAME.ALL_TRANSACTION_RESULTS_VOIDS
      case QuickFilter.STALE_DATED:
        return TABLE_NAME.ALL_TRANSACTION_RESULTS_STALE_DATED
      case QuickFilter.PAYEE_MATCH:
        return TABLE_NAME.ALL_TRANSACTION_RESULTS_PAYEE_MATCH
      case QuickFilter.ISSUED:
        return TABLE_NAME.ALL_TRANSACTION_RESULTS_ISSUED
      default:
        return ''
    }
  }, [])

  const getDataByFilter = useCallback(
    async (payload?: Partial<IGetTransactions.Payload>) => {
      try {
        setLoading(true)
        const data: Partial<IGetTransactions.Payload> = {
          ...formatFormValues(state),
          ...payload,
        }
        const res = await getTransactions(data as any)
        if (res.statusCode === STATUS_CODES.SUCCESS) {
          setTransactionState({
            items: res.data.items.map((item) => ({ ...item, amount: formatNumberWithCommas(item.amount) as any })),
            total: res.data.total,
          })
        }
        return res
      } catch (error) {
        return null
      } finally {
        setLoading(false)
      }
    },
    [formatFormValues, state],
  )

  const handleDownload = useCallback(
    async (payload: any) => {
      const data: Partial<IGetTransactions.Payload> = {
        ...formatFormValues(state),
        ...payload,
      }
      return await getTransactions(data as any)
    },
    [formatFormValues, state],
  )

  const handleSearch = useCallback(
    async (value: Partial<any>) => {
      saveSearchParams(value)
      await getDataByFilter(value)
    },
    [getDataByFilter, saveSearchParams],
  )

  useEffect(() => {
    if (state) {
      if (state.transactionType === QuickFilter.ACH) {
        setFilterState({
          selectedFilter: QuickFilter.ACH,
          disabledFilter: Object.values(QuickFilter).filter((item) => item !== QuickFilter.ACH) as any,
        })
      } else if (state.transactionType === QuickFilter.CHECKS) {
        setFilterState({
          selectedFilter: QuickFilter.CHECKS,
          disabledFilter: [QuickFilter.ACH],
        })
      }
    } else {
      // NOTE: navigate to all transactio landing screen when no state exists
      navigate(menuUrls.allTransactions)
    }
  }, [navigate, state])

  useEffect(() => {
    // NOTE: navigate to all transactio landing screen when no state exists
    if (!state) {
      navigate(menuUrls.allTransactions)
    }
  }, [navigate, state])

  useEffect(() => {
    // NOTE: navigate to all transactio landing screen when no state exists
    if (!state) {
      navigate(menuUrls.allTransactions)
    }
  }, [navigate, state])

  // Restore saved search params and UI state when component mounts
  useEffect(() => {
    if (defaultParams?.transactionType) {
      setFilterState((prev) => ({
        ...prev,
        selectedFilter: defaultParams.transactionType,
      }))
    }
    
    const savedUIState = restoreSearchParams()
    if (savedUIState) {
      const { searchColumns, searchTerm } = {searchColumns: savedUIState.searchColumns, searchTerm: savedUIState.searchTerm}
      if (searchColumns || searchTerm) {
        outerTableRef.current?.setSearchFormValues?.(
          searchTerm || '',
          searchColumns !== undefined && searchColumns?.length == 1 ? searchColumns[0] : 'All'
        )
        
        if (searchTerm || searchColumns?.length == 1) 
          outerTableRef.current?.setSearchOpen?.(true)
      }
    }
  }, [])

  return (
    <SearchResultContext.Provider
      value={{
        filterState,
        filterOptions,
        loading,
        transactionState,
        outerTableRef,
        defaultParams,
        setFilterState,
        setLoading,
        getTableName,
        handleSearch,
        handleDownload,
        generateCustomColumnDefs,
      }}
    >
      {children}
    </SearchResultContext.Provider>
  )
}

export default SearchResultProvider
